'use client'
import { useEffect, useState } from 'react'

interface UseIdleReadyOptions {
  /**
   * Max time (ms) to wait before `requestIdleCallback` forces the callback
   * to run even if the browser hasn't gone idle. Defaults to 1500.
   */
  timeout?: number
  /**
   * Delay (ms) used when `requestIdleCallback` is unavailable (Safari < 16.4,
   * older WebViews). Falls back to `setTimeout`. Defaults to 600.
   */
  fallbackDelay?: number
}

/**
 * Returns `false` on first render, then flips to `true` once the browser is
 * idle (via `requestIdleCallback`) or after `fallbackDelay` ms in environments
 * without it.
 *
 * Use this to defer non-critical work past first paint — for example, hydrating
 * offscreen carousel images, mounting heavy widgets below the fold, or kicking
 * off analytics work that would otherwise compete with LCP.
 *
 * The hook is SSR-safe: it returns `false` during render and only schedules the
 * idle callback inside `useEffect`, so the first server-rendered + client-
 * hydrated paint is always in the "not ready" state.
 *
 * @example
 * const isIdleReady = useIdleReady()
 * // Render first slide eagerly; defer rest until the browser breathes.
 * <Carousel>
 *   {items.map((item, i) => (
 *     <Slide key={item.id} shouldLoadImage={i === 0 || isIdleReady} />
 *   ))}
 * </Carousel>
 */
export function useIdleReady(options: UseIdleReadyOptions = {}): boolean {
  const { timeout = 1500, fallbackDelay = 600 } = options
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    if (typeof window.requestIdleCallback === 'function') {
      const handle = window.requestIdleCallback(() => setIsReady(true), { timeout })
      return () => window.cancelIdleCallback?.(handle)
    }

    const handle = window.setTimeout(() => setIsReady(true), fallbackDelay)
    return () => window.clearTimeout(handle)
  }, [timeout, fallbackDelay])

  return isReady
}
